import React, { useEffect, useRef, useState } from 'react';
interface CountdownProps {
  /** Total seconds to count down from. */
  initialSeconds?: number;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}

/** Ticks only near the viewport and remains accurate after backgrounding the tab. */
export function Countdown({
  initialSeconds = 23 * 3600 + 58 * 60 + 38
}: CountdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const endsAtRef = useRef(Date.now() + initialSeconds * 1000);
  const [remaining, setRemaining] = useState(initialSeconds);
  const [isVisible, setIsVisible] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(!document.hidden);
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      rootMargin: '120px'
    });
    observer.observe(element);
    const onVisibilityChange = () => setIsDocumentVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
  useEffect(() => {
    if (!isVisible || !isDocumentVisible) return undefined;
    let timer: number | undefined;
    const update = () => {
      const nextRemaining = Math.max(0, Math.ceil((endsAtRef.current - Date.now()) / 1000));
      setRemaining(nextRemaining);
      if (nextRemaining > 0) {
        timer = window.setTimeout(update, 1000 - Date.now() % 1000);
      }
    };
    update();
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [isDocumentVisible, isVisible]);
  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor(remaining % 3600 / 60);
  const seconds = remaining % 60;
  const units = [{
    value: pad(hours),
    label: 'Horas'
  }, {
    value: pad(minutes),
    label: 'Minutos'
  }, {
    value: pad(seconds),
    label: 'Segundos'
  }];
  return <div ref={containerRef} className="flex items-start justify-center gap-3 sm:gap-5" data-id="element-8" data-mp="x3yef6mrdsr8">
      {units.map((unit, index) => <React.Fragment key={unit.label} data-id="element-9" data-mp="1255jgxxiodu7" data-mpi={index}>
          <div className="flex flex-col items-center" data-id="element-10" data-mp="165fqmdx1paan">
            <div className="rounded-xl bg-black/50 px-4 py-3 font-display text-4xl leading-none text-lime shadow-inner sm:px-6 sm:py-4 sm:text-6xl" data-id="element-11" data-mp="1644la9nybuhf">
              {unit.value}
            </div>
            <span className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-white/60 sm:text-xs" data-id="element-12" data-mp="ef1kx4z6r4a2">
              {unit.label}
            </span>
          </div>
          {index < units.length - 1 && <span className="font-display text-4xl leading-none text-lime/60 sm:text-6xl" data-id="element-13" data-mp="n7v0l3ijo5og">:</span>}
        </React.Fragment>)}
    </div>;
}